/**
 * Film registry for conscierra.au/videos (Story #23825).
 *
 * DELIBERATELY EMPTY. The Conscierra explainer is still being re-checked, and no Conscierra
 * film is approved for publication. While this list is empty (BR-V-VID-003):
 *   - routes.tsx registers NO /videos route (the paths 404 like any unknown URL),
 *   - PublicLayout shows NO "Videos" nav item,
 *   - public/sitemap.xml carries NO /videos entry — VideosGate.test.tsx fails if it does.
 *
 * To publish a film once it is approved:
 *   1. add its copy to docs/specs/tsp-xcentral/working/videos-pages-2026-10-10.md §2 and the
 *      client-portal prototype first (the copy is verbatim from the spec, BR-V-VID-004);
 *   2. append an entry below — files are conscierra-<slug>.mp4 / -720.mp4 / .jpg;
 *   3. add /videos and /videos/<slug> to public/sitemap.xml (the test fails until you do);
 *   4. upload the three files (docs/runbooks/media-videos.md).
 * The route, nav item and prerender switch on automatically when the list is non-empty.
 */
import { MEDIA_BASE_URL } from '../../siteConfig';

export interface FilmFiles {
  /** 1080p MP4 (H.264/AAC), blob name. */
  hd: string;
  /** 720p MP4, blob name — offered to narrow viewports. */
  sd: string;
  /** Poster JPEG, blob name. */
  poster: string;
  /** English captions, WebVTT, blob name. */
  captions: string;
}

export interface Film {
  /** URL slug under /videos/ (no leading slash). */
  slug: string;
  /** Page H1 + card title. */
  title: string;
  /** The one line of copy shown under the film. */
  line: string;
  /** Which site the film belongs to. */
  brand: 'xcentral' | 'conscierra';
  files: FilmFiles;
}

/** Blob names for a Conscierra film: conscierra-<slug>.mp4 / -720.mp4 / .jpg. */
export function conscierraFiles(slug: string): FilmFiles {
  return {
    hd: `conscierra-${slug}.mp4`,
    sd: `conscierra-${slug}-720.mp4`,
    poster: `conscierra-${slug}.jpg`,
    captions: `conscierra-${slug}.en.vtt`,
  };
}

export const films: readonly Film[] = [];

/** True once at least one Conscierra film is approved — gates route, nav and sitemap. */
export const hasFilms = (): boolean => films.length > 0;

export function filmBySlug(slug: string): Film | undefined {
  return films.find((f) => f.slug === slug);
}

/** Absolute-path URL for a media blob, e.g. /media/conscierra-<slug>.mp4. */
export function mediaUrl(file: string): string {
  return `${MEDIA_BASE_URL.replace(/\/+$/, '')}/${file}`;
}
